import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../services/api';
import type { Provider } from '../types/provider';

/**
 * Inputs to `useProviders` — filter values + optional page size.
 *
 * `search` is the raw input value. The hook does NOT debounce; the
 * upstream `<SearchInput>` already debounces by 300ms, so by the
 * time `search` changes here the user has stopped typing.
 *
 * `groupId` is the FULL display name of an occupational group
 * (e.g. "Computer and mathematical sciences") — NOT a chip slug.
 * The Providers page resolves the slug → name before passing.
 */
export interface UseProvidersArgs {
  readonly search: string;
  readonly groupId: string;
  readonly metroId: string;
  readonly pageSize?: number;
}

/**
 * Snapshot returned by `useProviders`. Render branches:
 *   - `isLoading`     — show <LoadingState/> (initial fetch in progress)
 *   - `error`         — show <ErrorState onRetry={retry}/>
 *   - empty `items`   — show <EmptyState/> ("no matches")
 *   - otherwise      — render rows + sentinel that calls `loadMore`
 *
 * Pagination state:
 *   - `items` accumulates across pages within the same filter set
 *   - filter changes (search/groupId/metroId) reset items+offset
 *   - `loadMore` no-ops while loading or once `items.length >= total`
 *   - `hasMore = items.length < total` (true even during the initial
 *     load if the first page hasn't returned yet, because total=0;
 *     the consumer's IntersectionObserver hook should guard on
 *     !isLoading too)
 */
export interface UseProvidersResult {
  readonly items: ReadonlyArray<Provider>;
  readonly total: number;
  readonly isLoading: boolean;
  readonly isLoadingMore: boolean;
  readonly error: ApiError | null;
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  readonly retry: () => void;
}

/**
 * Fetch a paginated, filtered slice of /api/providers and accumulate
 * items as the consumer pages through.
 *
 * Design notes:
 *
 * 1. The initial fetch + filter-reset effect lives in the same
 *    `useEffect` block so the reset and refetch happen atomically
 *    (no flicker of stale data with new filters).
 *
 * 2. Cancellation via a `cancelled` ref guards against race
 *    conditions when filters change mid-fetch — the inflight
 *    request's resolution is dropped on the floor instead of
 *    overwriting fresh data.
 *
 * 3. `loadMore` reads its dependencies via a stable callback so
 *    consumers can pass it directly to an IntersectionObserver
 *    without re-creating the observer on every render.
 *
 * 4. Errors are normalized to `ApiError` so the consumer's render
 *    branch has one type to match. Non-ApiError throws (e.g. a
 *    programming error) are wrapped with code='UNKNOWN'.
 */
export function useProviders(args: UseProvidersArgs): UseProvidersResult {
  const { search, groupId, metroId, pageSize = 50 } = args;

  const [items, setItems] = useState<ReadonlyArray<Provider>>([]);
  const [total, setTotal] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [refetchToken, setRefetchToken] = useState<number>(0);

  // Latest offset/items length without forcing a re-create of
  // loadMore on every fetch resolution. The IntersectionObserver in
  // the consumer holds onto a single loadMore reference; if it
  // changed every page tick the observer would tear down + re-attach
  // mid-scroll. Using a ref for the "current accumulated offset"
  // lets loadMore stay stable while still reading the right value.
  const offsetRef = useRef<number>(0);
  const itemsLenRef = useRef<number>(0);
  const totalRef = useRef<number>(0);
  const inflightRef = useRef<boolean>(false);

  // WARN #2 generation guard. Every filter-change effect bumps the
  // counter; both the filter-change fetch and any concurrent loadMore
  // capture the counter at dispatch time. When a fetch resolves, it
  // checks whether its captured generation still matches the current
  // generation — if not, it's stale (a filter change happened
  // mid-flight) and its result is dropped. This prevents the loadMore
  // race where a page-N response appends 50 stale rows on top of the
  // 2 rows the new filter just produced.
  const generationRef = useRef<number>(0);

  // Filter-change effect: reset accumulator + fire first page.
  useEffect(() => {
    generationRef.current += 1;
    const gen = generationRef.current;
    setItems([]);

    offsetRef.current = 0;
    itemsLenRef.current = 0;
    totalRef.current = 0;
    setIsLoading(true);
    setError(null);
    inflightRef.current = true;

    api.providers
      .list({ search, groupId, metroId, limit: pageSize, offset: 0 })
      .then((res) => {
        if (gen !== generationRef.current) return;
        setItems(res.items);
        setTotal(res.total);

        offsetRef.current = res.items.length;
        itemsLenRef.current = res.items.length;
        totalRef.current = res.total;
      })
      .catch((cause: unknown) => {
        if (gen !== generationRef.current) return;
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError('UNKNOWN', String(cause), 0),
        );
      })
      .finally(() => {
        if (gen !== generationRef.current) return;
        setIsLoading(false);
        inflightRef.current = false;
      });

    return () => {
      // Unmount/filter-change: bump generation so any in-flight
      // resolution is recognized as stale by its captured `gen`
      // check above.
      generationRef.current += 1;
      inflightRef.current = false;
    };
  }, [search, groupId, metroId, pageSize, refetchToken]);

  const loadMore = useCallback(() => {
    if (inflightRef.current) return;
    if (itemsLenRef.current >= totalRef.current) return;
    inflightRef.current = true;
    setIsLoadingMore(true);
    const nextOffset = offsetRef.current;
    const gen = generationRef.current;

    api.providers
      .list({ search, groupId, metroId, limit: pageSize, offset: nextOffset })
      .then((res) => {
        // WARN #2 — stale page-N resolution after a filter change.
        // Drop on the floor. Once the cancellation guard exists, the
        // total should only update on filter change, not on loadMore
        // (resolves WARN #8 — stale page total clobbering fresh
        // filter total).
        if (gen !== generationRef.current) return;
        setItems((prev) => {
          const merged = [...prev, ...res.items];
          itemsLenRef.current = merged.length;
          return merged;
        });
        offsetRef.current = nextOffset + res.items.length;
      })
      .catch((cause: unknown) => {
        if (gen !== generationRef.current) return;
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError('UNKNOWN', String(cause), 0),
        );
      })
      .finally(() => {
        if (gen !== generationRef.current) return;
        setIsLoadingMore(false);
        inflightRef.current = false;
      });
  }, [search, groupId, metroId, pageSize]);

  const retry = useCallback(() => {
    // Bump the token to re-trigger the filter-change effect, which
    // already handles the reset + initial-page fetch. Cheaper than
    // duplicating the logic here.
    setRefetchToken((t) => t + 1);
  }, []);

  return {
    items,
    total,
    isLoading,
    isLoadingMore,
    error,
    hasMore: items.length < total,
    loadMore,
    retry,
  };
}
